import { useMemo, useState } from 'react'
import { AlertTriangle, MapPinned, ShieldCheck, ShieldOff, Users } from 'lucide-react'
import { ErrorState, KpiGrid, StatusBadge } from '@/components/common'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useGeofenceCoverage } from '../../api/geofence'
import { GEOFENCE_MODES } from '../../types/geofence'
import type { EffectivePolicyView } from '../../types/geofence'

type Filter = 'all' | 'mandatory' | 'free' | 'none' | 'warnings'

const FILTERS: Record<Filter, { label: string; test: (row: EffectivePolicyView) => boolean }> = {
  all: { label: 'Everyone', test: () => true },
  mandatory: { label: 'Must punch inside a fence', test: (row) => row.mandatory },
  free: { label: 'Can punch anywhere', test: (row) => row.mode === 'UNRESTRICTED' },
  none: { label: 'No policy', test: (row) => row.policyId == null },
  warnings: { label: 'With setup problems', test: (row) => row.warnings.length > 0 },
}

/**
 * Who has to be inside a fence, which policy put them there, and whether anything in the set-up
 * would turn them away at the door — the screen to check before switching a policy to BLOCK.
 */
export function CoverageTab() {
  const coverage = useGeofenceCoverage()
  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (coverage.data?.rows ?? [])
      .filter(FILTERS[filter].test)
      .filter((row) => !term
        || `${row.employeeName ?? ''} ${row.employeeCode} ${row.departmentName ?? ''} ${row.policyName ?? ''}`
          .toLowerCase().includes(term))
  }, [coverage.data, filter, search])

  if (coverage.isPending) return <Skeleton className="h-64" />
  if (coverage.isError) return <ErrorState error={coverage.error} onRetry={() => void coverage.refetch()} />
  const data = coverage.data

  return (
    <div className="space-y-4">
      <KpiGrid items={[
        { label: 'Employees', value: data.employees, icon: Users, tint: 'purple' },
        { label: 'Geofence mandatory', value: data.mandatory, icon: ShieldCheck, tint: 'green' },
        { label: 'Can punch anywhere', value: data.unrestricted, icon: MapPinned, tint: 'blue' },
        { label: 'No policy', value: data.uncovered, caption: 'Old behaviour: site named by the phone', icon: ShieldOff, tint: 'orange' },
        { label: 'Setup problems', value: data.withWarnings, caption: 'Could be locked out', icon: AlertTriangle, tint: 'red' },
      ]} />

      <div className="flex flex-col gap-2 sm:flex-row">
        <Input aria-label="Search employees" placeholder="Search name, code, department or policy…"
          value={search} onChange={(event) => setSearch(event.target.value)} />
        <Select aria-label="Show" value={filter} className="sm:w-64"
          onChange={(event) => setFilter(event.target.value as Filter)}>
          {(Object.keys(FILTERS) as Filter[]).map((key) => (
            <option key={key} value={key}>{FILTERS[key].label}</option>
          ))}
        </Select>
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] text-xs text-[var(--color-muted)]">
              <th className="p-3 text-start font-medium">Employee</th>
              <th className="p-3 text-start font-medium">Work location</th>
              <th className="p-3 text-start font-medium">Policy</th>
              <th className="p-3 text-start font-medium">May punch at</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 300).map((row) => (
              <tr key={row.employeeId} className="border-b border-[var(--color-border)] align-top last:border-0">
                <td className="p-3">
                  <p className="font-medium text-[var(--color-heading)]">{row.employeeName}</p>
                  <p className="text-xs text-[var(--color-muted)]">
                    {row.employeeCode}{row.departmentName ? ` · ${row.departmentName}` : ''}
                  </p>
                </td>
                <td className="p-3">{row.locationName ?? '—'}</td>
                <td className="p-3">
                  {row.policyName
                    ? (
                      <>
                        <p className="text-[var(--color-heading)]">{row.policyName}</p>
                        <p className="text-xs text-[var(--color-muted)]">{row.source}</p>
                      </>
                    )
                    : <StatusBadge status="NONE" tone="neutral" label="No policy" />}
                </td>
                <td className="p-3">
                  {row.mode === 'UNRESTRICTED' && <StatusBadge status="FREE" tone="info" label="Anywhere" />}
                  {row.mode && row.mode !== 'UNRESTRICTED' && (
                    <>
                      <p className="text-xs text-[var(--color-muted)]">{GEOFENCE_MODES[row.mode].label}</p>
                      <p className="text-[var(--color-heading)]">
                        {row.permittedSites.length
                          ? row.permittedSites.map((site) => site.name + (site.fenced ? '' : ' (no fence)')).join(', ')
                          : '—'}
                      </p>
                    </>
                  )}
                  {row.warnings.map((warning) => (
                    <p key={warning} className="mt-1 flex items-start gap-1 text-xs text-[var(--color-danger)]">
                      <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                      {warning}
                    </p>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 300 && (
          <p className="p-3 text-xs text-[var(--color-muted)]">
            Showing the first 300 of {rows.length}. Search or filter to narrow it down.
          </p>
        )}
        {rows.length === 0 && <p className="p-4 text-sm text-[var(--color-muted)]">Nobody matches.</p>}
      </Card>
    </div>
  )
}
